import './App.css';

import React from 'react';
import {
  Route,
  BrowserRouter,
  Navigate,
  Routes,
  Outlet,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { useAuth, AuthProvider } from 'react-oidc-context';

import Login from './Components/Login/Login';
import { StaticDataProvider } from './StaticDataProvider';
import { Product } from './Components/Product/Product';
import { Device } from './Components/Device/Device';
import { Firmware } from './Components/Firmware/Firmware';
import { NavBar } from './Components/NavBar/NavBar';
import { ProductOverview } from './Components/ProductOverview/ProductOverview';
import { ServicesOverview } from './Components/ServicesOverview/ServicesOverview';
import { UsersOverview } from './Components/Users/UsersOverview';
import { OidcUserManager, isDevMode } from './Components/Auth/auth';
import { DevAuthLogin, useDevAuth } from './Components/Auth/DevAuth';
import { useSaveLastVisitedUrl } from './Components/Hooks/urlPersistence';

function App() {
  // If in dev mode, use a simpler routing without OIDC
  if (isDevMode) {
    return (
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<DevAuthLogin />} />
          <Route path="/" element={<DevProtectedLayout />}>
            <Route path="/products" element={<ProductOverview />} />
            <Route path="/services" element={<ServicesOverview />} />
            <Route path="/users" element={<UsersOverview />} />
            <Route
              path="/Products/:productCode/Variant/:variant/Firmware/:firmwareVersion"
              element={<Firmware />}
            />
            <Route
              path="/Products/:productCode/Devices/:deviceId"
              element={<Device />}
            />
            <Route
              path="/Products/:productCode/Variant/:variant"
              element={<Product />}
            />
          </Route>
        </Routes>
      </BrowserRouter>
    );
  }

  // Production mode with OIDC
  return (
    <AuthProvider userManager={OidcUserManager}>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<ProtectedLayout />}>
            <Route path="/products" element={<ProductOverview />} />
            <Route path="/services" element={<ServicesOverview />} />
            <Route path="/users" element={<UsersOverview />} />
            <Route
              path="/Products/:productCode/Variant/:variant/Firmware/:firmwareVersion"
              element={<Firmware />}
            />
            <Route
              path="/Products/:productCode/Devices/:deviceId"
              element={<Device />}
            />
            <Route
              path="/Products/:productCode/Variant/:variant"
              element={<Product />}
            />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

// Dev mode protected layout (no OIDC)
const DevProtectedLayout = () => {
  const { isAuthenticated } = useDevAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hasNavigatedRef = React.useRef(false);

  useSaveLastVisitedUrl();

  React.useEffect(() => {
    if (location.pathname === '/' && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      navigate('/products', { replace: true });
    }
  }, [location.pathname, navigate]);

  if (!isAuthenticated) {
    return (
      <Navigate to="/login" replace state={{ from: location }} />
    );
  }

  if (location.pathname === '/') {
    return <div>Loading...</div>;
  }

  return (
    <StaticDataProvider>
      <NavBar />
      <Outlet />
    </StaticDataProvider>
  );
};

// Production OIDC protected layout
export const ProtectedLayout = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hasNavigatedRef = React.useRef(false);

  useSaveLastVisitedUrl();

  React.useEffect(() => {
    if (location.pathname === '/' && !hasNavigatedRef.current) {
      hasNavigatedRef.current = true;
      navigate('/products', { replace: true });
    }
  }, [location.pathname, navigate]);

  if (!isAuthenticated) {
    // user is not authenticated
    return (
      <Navigate to="/login" replace state={{ from: location }} />
    );
  }

  if (location.pathname === '/') {
    return <div>Loading...</div>;
  }

  return (
    <StaticDataProvider>
      <NavBar />
      <Outlet />
    </StaticDataProvider>
  );
};

export default App;
